import { Columns } from '@domain-group/suit-theme'

import useProvider from '@hooks/useProvider'

import ApiKeys from '@components/ApiKeys'
import EntityUsers from '@components/EntityUsers'
import MessagePanel from '@components/MessagePanel'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import TemplatePageList from '@components/TemplatePageList'

import PageLayout from '../../../../components/PageLayout'
import ProviderTools from '../../../../components/ProviderTools'

import BrandProviders from '../BrandProviders'
import DeepLinks from '../DeepLinks'
import Disclaimers from '../Disclaimers'
import OrderBundles from '../OrderBundles'
import OrderCodes from '../OrderCodes'
import OrderLeadTimes from '../OrderLeadTimes'
import Products from '../Products'
import ProviderDeadlineGroups from '../ProviderDeadlineGroups'
import ProviderPackages from '../ProviderPackages'
import ProviderPriceLists from '../ProviderPriceLists'
import Services from '../Services'
import Webhooks from '../Webhooks'

interface Props {
  providerId: number,
}

const PageContent = (props: Props) => {
  const { providerId } = props

  const providerPayload = useProvider({ id: providerId })
  const { provider } = providerPayload
  const zuoraAccountNumber = provider?.zuora_account_number || null

  return (
    <PageLayout entityPayload={providerPayload} activeTabBarItemKey="permissions">
      <Columns.Main>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          {!zuoraAccountNumber && (
          <MessagePanel
            description={`This provider does not have a Zuora Account Number stored
              which means no invoices can be generated against this provider. If
              this provider should be invoiced for any reason, please have the
              provider added to Salesforce. Reach out to the Realhub Onboarding 
              Specialist team to correctly link this account to Salesforce & Zuora.`}
            entityProperty
            marginBottom="large"
            title="Non Billable Provider"
          />
          )}
        </ScopedConditionalContent>

        {/*
         // @ts-expect-error TS2741 */}
        <ApiKeys
          filters={{
            owner_id: providerId,
            owner_type: 'Provider',
          }}
          userSearchRequestOptions={{ provider_id: providerId }}
        />

        <BrandProviders providerId={providerId} />

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <OrderBundles providerId={providerId} />
        </ScopedConditionalContent>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Provider Director', 'Support']}>
          <DeepLinks providerPayload={providerPayload} />
        </ScopedConditionalContent>

        <Disclaimers providerId={providerId} />

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <OrderLeadTimes providerId={providerId} />
        </ScopedConditionalContent>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Provider Director', 'Support']}>
          <OrderCodes providerId={providerId} />

          <TemplatePageList
            allowedFilters={['agency_id', 'service_product_id', 'brand_id']}
            filterIds={{ provider_id: providerId }}
          />
        </ScopedConditionalContent>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <ProviderPackages providerId={providerId} />

          <ProviderPriceLists providerId={providerId} />

          <Products providerId={providerId} />

          <EntityUsers
            // @ts-expect-error TS2741
            filters={{ provider_id: providerId }}
            initUser={{ association_id: providerId, association_type: 'Provider' }}
            performHttpRequests={!!providerId}
          />
        </ScopedConditionalContent>

        <Webhooks providerId={providerId} />
      </Columns.Main>

      <Columns.Sidebar>
        <ProviderTools providerPayload={providerPayload} />

        <Services providerId={providerId} />

        <ProviderDeadlineGroups providerId={providerId} />
      </Columns.Sidebar>
    </PageLayout>
  )
}

export default PageContent
